import { useState } from "react";
import Modal from 'react-modal';

import { createProfile } from "helpers/profiles";
import { useLocalisationService } from "providers/LocalisationProvider";
import { useCurrentUserSnapshot } from "hooks/useAccount";
import { useModalScrollLock } from 'hooks/useModalScrollLock';
import { ProfileIcons } from 'fixit-shared/Consts';

Modal.setAppElement('#root');

export const CreateProfileModal = ({ isOpen, onClose, onCreatedProfile, cloudFunctions }) => {

    useModalScrollLock(isOpen);

    const l = useLocalisationService();
    const [requestInFlight, setRequestInFlight] = useState(false);

    const [displayName, setDisplayName] = useState("");
    const [raceIcons, setRaceIcons] = useState("");
    const [genderIcons, setGenderIcons] = useState("");
    const [consentObtained, setConsentObtained] = useState(false);
    const [displayIconPulse, setDisplayIconPulse] = useState(false);
    const [displayNamePulse, setDisplayNamePulse] = useState(false);
    const [displayConsentPulse, setDisplayConsentPulse] = useState(false);
    const user = useCurrentUserSnapshot();
    
    const isComplete = () => {
        return displayName && raceIcons && genderIcons && consentObtained;
    }

    const renderCreateDialogue = () => {
        const confirmStyle = `create-profile-button ${isComplete()?'primary':'disabled'}-button rounded`;

        return (
            <Modal
                className="modal create-profile-modal"
                overlayClassName="modal-overlay create-profile-modal-overlay"
                isOpen={isOpen}
                onRequestClose={closeModal}
                contentLabel="Create Profile"
            >
    
            <form className="modal-flex launch-form" onSubmit={(e)=>e.preventDefault()}> 
                {renderIcons()}
                {renderNameEntry()}
                {renderConsentConfirmation()}
            </form>
            <div className="modal-row">                 
                <button className="create-profile-button secondary-button rounded" onClick={closeModal}>
                    {l.getString("cancel")}
                </button>                
                <button className={confirmStyle} onClick={handleCreateProfile}>
                    {l.getString("confirm")}
                </button>
            </div>
          </Modal>);
    }

    const renderInFlightDialogue = () => {
        return <Modal
        className="modal create-profile-modal"
        overlayClassName="modal-overlay create-profile-modal-overlay"
        isOpen={isOpen}
        onRequestClose={closeModal}
        contentLabel="Create Profile">
            <div className="flexbox centered full-height">
                {l.getString('profile_create_in_flight')}
            </div>
        </Modal>;
    }
    
    const renderNameEntry = () => {
        const style = displayNamePulse ? "rounded pulse-red-inset" : "rounded"
        return (
            <div className="modal-row">
                <div className="modal-flex">
                    {l.getString("profile_create_name_title")}
                    <div className="modal-flex centered">
                        <input className={style} type="text" value={displayName} placeholder={l.getString("profile_create_name_hint")} onChange={handleNameChange} />
                    </div>
                </div>
            </div>
        );
    }

    const renderConsentConfirmation = () => {
        const style = displayConsentPulse ? "rounded pulse-red-inset" : "rounded"
        return (
            <div className={"modal-row"}>
                <div className="flexbox">
                    <input className={style} type="checkbox" value={consentObtained} onChange={handleConsentChange} />
                    <span className="consent-confirm" >{l.getString("profile_obtained_consent")}</span>
                </div>
            </div>
        );
    }

    const renderIcons = () => {
        return (
        <div className="modal-row">
            <div className="modal-flex centered">
                <p> {l.getString("profile_create_icon_title")}</p>
                <div className="flexbox">
                    {renderIcon(ProfileIcons.WhiteMan, "White", "Man")}
                    {renderIcon(ProfileIcons.BlackMan, "Black", "Man")}
                    {renderIcon(ProfileIcons.WhiteWoman, "White", "Woman")}
                    {renderIcon(ProfileIcons.BlackWoman, "Black", "Woman")}
                </div>
            </div>
        </div>);
    }

    const renderIcon = (icon, race, gender) => {
        const textKey = `${race.toLowerCase()}_${gender.toLowerCase()}_option`
        return (
            <img className={iconStyle(race, gender)} src={icon} alt={l.getString(textKey)} onClick={() => handleIconChange(race, gender)}/>
        )
    }

    const resetProfile = () => {
        setDisplayName("");
        setRaceIcons("");
        setGenderIcons("");
        setConsentObtained(false);
        setDisplayIconPulse(false);
        setDisplayNamePulse(false);
        setDisplayConsentPulse(false);
    }

    const buildProfileObject = () => {
        return {
            displayName, 
            raceIcons,
            genderIcons,
            consentObtained,
            incidents: [],
            version: 2
        }
    }

    const handleCreateProfile = () => {        
        if(!isComplete()){
            setPulses();
            return;
        }
        setRequestInFlight(true);
        
        createProfile(cloudFunctions, user, buildProfileObject())
        .then(outcome => {
            resetProfile();
            setRequestInFlight(false);
            onCreatedProfile(outcome);
        });
    }

    const setPulses = () => {
        if(!raceIcons && !genderIcons){
            setDisplayIconPulse(true);
        }
        if(!consentObtained){
            setDisplayConsentPulse(true);
        }
        if(!displayName){
            setDisplayNamePulse(true);
        }
        setTimeout( () => {
            setDisplayIconPulse(false);
            setDisplayConsentPulse(false);
            setDisplayNamePulse(false);
          }, 1000);
    }

    const iconStyle = (selectedRace, selectedGender) => {
        if(displayIconPulse) return "icon-option pulse-red";
        return (selectedRace === raceIcons && selectedGender === genderIcons) ? "icon-option selected-icon" : "icon-option";
    }

    const handleNameChange = (event) => {
        if(displayNamePulse){
            setDisplayNamePulse(false);
        }
        setDisplayName(event.target.value);
    }

    const handleConsentChange = (event) => {
        if(displayConsentPulse){
            setDisplayConsentPulse(false);
        }
        setConsentObtained(event.target.checked);
    }

    const handleIconChange = (raceIcons, genderIcons) => {
        if(displayIconPulse){
            setDisplayIconPulse(false);
        }
        setRaceIcons(raceIcons);
        setGenderIcons(genderIcons);
    }

    const closeModal = () => {
        resetProfile();
        setRequestInFlight(false);
        onClose();
    }

    if(requestInFlight){
        return renderInFlightDialogue();
    }
    else{
        return renderCreateDialogue();
    }
}